import { filterConfig } from './filterConfig';

// ============================================================
// Per-layer filter mapping para parques/zonas
//
// El problema: el WHERE de un filtro solo es válido en los sublayers
// cuyo schema contiene el campo. Por ejemplo `id_ocupacion` solo existe
// en parques /4 (Unidades Inmobiliarias). Si aplicamos `id_ocupacion = 1`
// como un WHERE global, los otros sublayers fallan al cargar features.
//
// Cada wrapper (ParqueSublayer/ZonaSublayer) usa buildLayerWhere para
// construir SU PROPIO where, incluyendo solo los filtros que aplican
// a su layer id.
// ============================================================

// Map: (tabKey, SQL clave) → lista de layer ids donde el campo existe.
// IMPORTANTE: las keys aquí son el SQL field (id_ocupacion, idUso, etc.) NO
// el nombre del filtro en filterConfig (disponibilidad, uso). FilterPanel
// guarda los filtros con clave por SQL field — así que estos keys deben
// matchear lo que sale del state de filtros.
export const FILTER_LAYER_SUPPORT = {
  parquesIndustriales: {
    id_ocupacion: [4], // solo /4 (lotes) tiene este campo
    idUso: [4],        // solo /4
    __area: [4],       // filtro de área solo aplica a lotes /4
    // tipo_convenio no existe en ningún sublayer — se omite a propósito
  },
  zonasIndustriales: {
    id_zona: [0],                // solo /0 (lotes)
    id_uso: [0],                 // solo /0
    id_ocupacion: [0],           // solo /0
    id_zonificacion: [0, 1, 2],  // en todas las sublayers
  },
};

// Mapeo de rangos de área (UI key → SQL clause sobre campo `area`).
export const getAreaWhere = (rangeValue) => {
  switch (rangeValue) {
    case "lt1000":      return "area < 1000";
    case "1000_2499":   return "area >= 1000 AND area < 2500";
    case "2500_4999":   return "area >= 2500 AND area < 5000";
    case "5000_10000":  return "area >= 5000 AND area <= 10000";
    case "gt10000":     return "area > 10000";
    default:            return "1=1";
  }
};

// Construye el WHERE para un sublayer específico a partir del state de
// filtros raw. Solo incluye cláusulas cuyo SQL field existe en ese layer
// según FILTER_LAYER_SUPPORT. La key del filters ya ES el SQL field
// (porque FilterPanel.handleChange guarda bajo `clave`), así que la usamos
// directo en la cláusula.
export const buildLayerWhere = (tabKey, layerId, filters) => {
  const support = FILTER_LAYER_SUPPORT[tabKey] || {};
  const clauses = [];

  Object.entries(filters || {}).forEach(([key, values]) => {
    // Saltamos las propiedades especiales (__text para búsqueda de texto,
    // __area se maneja por separado más abajo).
    if (key.startsWith("__")) return;
    if (!Array.isArray(values) || values.length === 0) return;
    if (!(support[key] || []).includes(layerId)) return;

    // Si el valor es string lo quoteamos, si es número lo dejamos crudo.
    // Para arrays mixtos quoteamos todos a string por seguridad.
    const formatValue = (v) =>
      typeof v === "number" ? String(v) : `'${String(v).replace(/'/g, "''")}'`;

    if (values.length === 1) {
      clauses.push(`${key} = ${formatValue(values[0])}`);
    } else {
      const inValues = values.map(formatValue).join(",");
      clauses.push(`${key} IN (${inValues})`);
    }
  });

  // Filtro de área (clave reservada __area). Aplica solo a layers donde
  // el campo `area` realmente existe.
  if (
    filters?.__area &&
    filters.__area !== "all" &&
    (support.__area || []).includes(layerId)
  ) {
    const areaWhere = getAreaWhere(filters.__area);
    if (areaWhere && areaWhere !== "1=1") clauses.push(areaWhere);
  }

  return clauses.length ? clauses.join(" AND ") : "1=1";
};


export const convertFiltersToWhereClause = (filters, activeTab) => {
  if (!filters || Object.keys(filters).length === 0) {
    return "1=1"; // Sin filtros, mostrar todo
  }

  const tabMapping = {
    0: "mercadosMinoristas",
    1: "mercadosMayoristas",
    2: "parquesIndustriales",
    3: "zonasIndustriales",
  };

  const configKey = tabMapping[activeTab];
  const config = filterConfig[configKey];

  if (!config) {
    return "1=1";
  }

  const whereConditions = [];

  Object.entries(filters).forEach(([fieldName, values]) => {
    if (!Array.isArray(values) || values.length === 0) return;
    // Ubigeo se construye aparte con LIKE — saltar los nombres de nivel.
    if (fieldName === "departamento" || fieldName === "provincia" || fieldName === "distrito") return;

    let fieldConfig = null;
    let actualFieldName = fieldName;

    Object.entries(config).forEach(([configField, options]) => {
      if (Array.isArray(options) && options.length > 0) {
        const option = options.find(opt => opt.clave === fieldName);
        if (option) {
          fieldConfig = option;
          actualFieldName = fieldName;
        }
      }
    });

    if (values.length === 1) {
      whereConditions.push(`${actualFieldName} = ${values[0]}`);
    } else {
      const inValues = values.map(v => v.toString()).join(',');
      whereConditions.push(`${actualFieldName} IN (${inValues})`);
    }
  });

  const ubigeoWhere = buildUbigeoWhere(filters);
  if (ubigeoWhere) whereConditions.push(ubigeoWhere);

  return whereConditions.length > 0 ? whereConditions.join(' AND ') : "1=1";
};

export const shouldApplyFiltersToLayer = (activeTab, layerType) => {
  const tabToLayerMapping = {
    0: 'minoristas',
    1: 'mayoristas',
    2: 'parques',
    3: 'zonas'
  };

  return tabToLayerMapping[activeTab] === layerType;
};

// Ubigeo se modela como un único campo `id_ubigeo` (código de distrito, 6 chars).
// Departamento → prefijo de 2; Provincia → prefijo de 4; Distrito → match exacto.
// Cuando hay varios niveles seleccionados, el más específico ya implica los demás
// (un distrito sólo puede pertenecer a una provincia / departamento), así que
// usamos sólo el nivel más estricto disponible.
const buildUbigeoWhere = (filters) => {
  const dist = filters?.distrito?.[0];
  const prov = filters?.provincia?.[0];
  const dep = filters?.departamento?.[0];
  const pick = (v) => (v == null || v === "" ? null : String(v));
  if (pick(dist)) return `id_ubigeo = '${pick(dist)}'`;
  if (pick(prov)) return `id_ubigeo LIKE '${pick(prov)}%'`;
  if (pick(dep)) return `id_ubigeo LIKE '${pick(dep)}%'`;
  return null;
};

export const buildWhereFromFilters = (filters) => {
  const clauses = [];

  Object.entries(filters).forEach(([key, value]) => {
    // Ignorar campos especiales y ubigeo (se construye aparte con LIKE)
    if (key === "__rawWhere") return;
    if (key === "departamento" || key === "provincia" || key === "distrito") return;

    if (!Array.isArray(value) || value.length === 0) return;

    if (value.length === 1) {
      clauses.push(`${key} = ${value[0]}`);
    } else {
      const inValues = value.map(v => v.toString()).join(',');
      clauses.push(`${key} IN (${inValues})`);
    }
  });

  const ubigeoWhere = buildUbigeoWhere(filters);
  if (ubigeoWhere) clauses.push(ubigeoWhere);

  return clauses.length ? clauses.join(" AND ") : "1=1";
};
